import React, { useEffect, useState } from 'react';
import Button from '../../ui/Button/Button';
import Dialog from '../../ui/Dialog/Dialog';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import LoadingSpinner from '../../components/LoadingSpinner';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { CellText } from '../../ui/DataTable/cells';
import { finesService, errorMessage } from '../../services/finesService';
import { useRowLinks, stickyTop } from '../TrafficFines/components/jobLinks';
import { Pill } from '../TrafficFines/components/ui';

// "Late trips now" (owner, 7 Oct 2026): every contract with Salik / Darb trips not on Ejar for a day the night's pass
// already closed - listed first (nothing written), then, on Write, the same top-up the 30-minute pull gives a new late
// trip: the contract's line made up to the last closed day. The current day is never touched; monthly / leasing /
// Invygo contracts keep their Saturday line (or are written by hand) and are only listed here.

const MONTHLY = new Set(['Monthly', 'Leasing', 'Invygo']);
const STATUS = { written: ['Written', 'green'], upToDate: ['Up to date', 'blue'], failed: ['Failed', 'red'] };

export default function LateTripsModal({ onClose }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState(null);
    const { ContractLink, modals } = useRowLinks();

    const load = async (write) => {
        setBusy(write ? 'write' : 'list'); setError(null);
        try { setData(await finesService.ejarAutoSalikTopup(write ? { write: true } : {})); } catch (e) { setError(errorMessage(e)); }
        setBusy('');
    };
    useEffect(() => { load(false); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

    const contracts = Object.entries(data?.contracts || {}).map(([contract, v]) => ({ contract, ...v }));
    const results = {};
    for (const r of data?.run?.results || []) results[r.contract] = r;
    const run = data?.run;
    const daily = data?.daily || 0;
    const monthlyWhen = data?.monthlyByHand ? 'by hand' : 'Saturday';   // Admin > Schedules: never = written by hand

    return (
        <Dialog open onClose={onClose} size="xl" title="Late trips now - Salik / Darb trips of closed days not on Ejar">
            <div className={stickyTop}>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                    {data && (
                        <span className="text-gray-700">
                            Days closed by the night's pass up to <b>{data.closed}</b> · <b>{data.count}</b> contracts ·
                            <b className="ml-1">{daily}</b> daily (written on Write) · <b>{data.monthly || 0}</b> monthly / leasing / Invygo ({data.monthlyByHand ? 'written by hand' : 'their Saturday line'})
                        </span>
                    )}
                    <span className="flex-1" />
                    <Button variant="secondary" size="sm" onClick={() => load(false)} disabled={!!busy} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Refresh</Button>
                    <Button variant="primary" size="sm" onClick={() => load(true)} disabled={!!busy || !data || daily === 0 || !!run}
                            leadingIcon={<Icon glyph={ICONS.upload} size="sm" />}
                            title="Write the closed days' trips on each daily contract's Salik line now - the current day never; the night's pass runs as always">
                        {run ? 'Written' : `Write ${daily} contract${daily === 1 ? '' : 's'} on Ejar`}
                    </Button>
                </div>
                {run && (
                    <div className="mt-2 text-sm text-gray-700">
                        {run.skipped ? <span className="text-amber-700">Not run: {run.skipped}</span> : (
                            <span>Result: <b>{run.written || 0}</b> written · <b>{run.upToDate || 0}</b> up to date · <b className={run.failed ? 'text-red-700' : ''}>{run.failed || 0}</b> failed</span>
                        )}
                    </div>
                )}
                {error && <div role="alert" className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            </div>
            {busy === 'list' && <LoadingSpinner message="Reading the trips not on Ejar…" />}
            {busy === 'write' && <LoadingSpinner message="Writing the lines on Ejar, one contract at a time…" />}
            {data && !busy && (contracts.length === 0 ? <div className="text-sm text-gray-500 mt-2">Every closed day's trip is on Ejar.</div> : (
                <TableContainer maxHeight={560}>
                    <Table sticky>
                        <TableHead><TableRow>
                            <TableHeader>#</TableHeader><TableHeader>Contract</TableHeader><TableHeader>Kind</TableHeader><TableHeader>Trips not on Ejar</TableHeader>
                            <TableHeader>Written now</TableHeader><TableHeader>Result</TableHeader>
                        </TableRow></TableHead>
                        <TableBody>{contracts.map((c, i) => {
                            const res = results[c.contract];
                            const monthly = MONTHLY.has(c.kind);
                            const line = (res?.lines || []).filter((l) => l.result === 'written').map((l) => l.new?.comment).filter(Boolean).join(' · ');
                            return (
                                <TableRow key={c.contract}>
                                    <TableCell><span className="ds-cell-num">{i + 1}</span></TableCell>
                                    <TableCell><ContractLink contract={c.contract} /></TableCell>
                                    <TableCell><Pill tone={monthly ? 'gray' : 'purple'} size="sm">{c.kind}{monthly ? ` · ${monthlyWhen}` : ''}</Pill></TableCell>
                                    <TableCell><span className="ds-cell-num">{c.trips}</span></TableCell>
                                    <TableCell>{res ? <Pill tone={STATUS[res.status]?.[1] || 'gray'} size="sm">{STATUS[res.status]?.[0] || res.status}</Pill> : <CellText>{monthly ? (data.monthlyByHand ? 'by hand' : 'waits for Saturday') : run ? '—' : 'on Write'}</CellText>}</TableCell>
                                    <TableCell><CellText>{res?.error || line || ''}</CellText></TableCell>
                                </TableRow>);
                        })}</TableBody>
                    </Table>
                </TableContainer>
            ))}
            {modals}
        </Dialog>
    );
}
